import { TCommentListItem } from 'components/CommentsWidget/types';
import {
  TMarkWorkAsImportantParams,
  TCreateWorkCommentPayload,
  TCreateWorkPayload,
  TDeleteWorkCommentPayload,
  TFetchWorksListQueryParams,
  TWorkListItem,
  TWorkVotesListItem,
  TAssignMemberToWorkPayload,
  TUpdateWorkPayload,
} from 'features/work/types';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helpers';
import { ListQueryResponse } from 'lib/rtkQuery/types';
import qs from 'qs';

const endpoint = `/work`;

export const workApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      QueryTags.WorkList,
      QueryTags.WorkDetail,
      QueryTags.WorkComments,
    ],
  })
  .injectEndpoints({
    endpoints: builder => ({
      CreateWork: builder.mutation<TWorkListItem, TCreateWorkPayload>({
        query: data => ({
          url: endpoint,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.WorkList,
          QueryTags.InternalDocumentDetail,
        ]),
      }),

      UpdateWork: builder.mutation<void, TUpdateWorkPayload>({
        query: ({ workCode, data }) => ({
          url: `${endpoint}/${workCode}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.WorkDetail,
          QueryTags.WorkList,
        ]),
      }),

      FetchWorkList: builder.query<
        ListQueryResponse<TWorkListItem[]>,
        TFetchWorksListQueryParams
      >({
        query: params => {
          const queryParams = qs.stringify(params, {
            arrayFormat: 'repeat',
            encode: false,
          });
          return {
            url: `${endpoint}?${queryParams}`,
          };
        },
        providesTags: [QueryTags.WorkList],
      }),

      GetWorkDetail: builder.query<any, string>({
        query: workCode => ({
          url: `${endpoint}/${workCode}`,
        }),
        providesTags: [QueryTags.WorkDetail],
      }),

      GetWorkComments: builder.query<TCommentListItem[], string>({
        query: workCode => ({
          url: `${endpoint}/${workCode}/comments`,
        }),
        providesTags: [QueryTags.WorkComments],
      }),

      CreateWorkComment: builder.mutation<void, TCreateWorkCommentPayload>({
        query: ({ workCode, data }) => ({
          url: `${endpoint}/${workCode}/comments`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.WorkComments]),
      }),

      GetWorkVotes: builder.query<TWorkVotesListItem[], string>({
        query: workCode => ({
          url: `${endpoint}/${workCode}/votes`,
        }),
      }),

      DeleteWorkComment: builder.mutation<void, TDeleteWorkCommentPayload>({
        query: ({ workCode, commentId }) => ({
          url: `${endpoint}/${workCode}/comments/${commentId}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.WorkComments]),
      }),

      DeleteWork: builder.mutation<void, string>({
        query: workCode => ({
          url: `${endpoint}/${workCode}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.WorkList]),
      }),

      MarkWorkAsImportant: builder.mutation<void, TMarkWorkAsImportantParams>({
        query: ({ work_code, is_important }) => ({
          url: `${endpoint}/${work_code}/mark_important/${is_important}`,
          method: 'PUT',
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.WorkDetail,
          QueryTags.WorkList,
        ]),
      }),

      AssignMemberToWork: builder.mutation<void, TAssignMemberToWorkPayload>({
        query: ({ work_code, ...data }) => ({
          url: `${endpoint}/${work_code}/update-people-invole`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.WorkDetail,
          QueryTags.WorkList,
        ]),
      }),
    }),
  });

export const {
  useCreateWorkMutation,
  useFetchWorkListQuery,
  useGetWorkDetailQuery,
  useGetWorkCommentsQuery,
  useCreateWorkCommentMutation,
  useGetWorkVotesQuery,
  useDeleteWorkCommentMutation,
  useDeleteWorkMutation,
  useMarkWorkAsImportantMutation,
  useAssignMemberToWorkMutation,
  useUpdateWorkMutation,
} = workApi;
